/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import { safeNumber } from "@/lib/utils/formatters";
import type { Database } from "@/lib/supabase/types";
import type { SupabaseClient } from "@supabase/supabase-js";
import { logger } from "@/lib/utils/logger";

export type BudgetAssumptionsInput = {
  total_budget?: number | string | null;
  revenue_target?: number | string | null;
  profit_margin?: number | string | null;
  overhead_rate?: number | string | null;
  contingency_rate?: number | string | null;
  labor_allocation?: number | string | null;
  labor_burden_rate?: number | string | null;
  labor_budget_mode?: "individual" | "blended" | null;
  blended_labor_rate?: number | string | null;
  material_allocation?: number | string | null;
  equipment_allocation?: number | string | null;
  subcontractor_allocation?: number | string | null;
  overhead_allocation?: number | string | null;
  crew_man_hours?: number | string | null;
};

export type BudgetDerivedValues = {
  profitAmount: number;
  overheadAmount: number;
  contingencyAmount: number;
  netBudget: number;
};

export function calculateBudgetDerivedValues(
  assumptions: BudgetAssumptionsInput,
): BudgetDerivedValues {
  const totalBudget = safeNumber(assumptions.total_budget);
  const revenueTarget = safeNumber(assumptions.revenue_target);
  const profitMargin = safeNumber(assumptions.profit_margin);
  const overheadRate = safeNumber(assumptions.overhead_rate);
  const contingencyRate = safeNumber(assumptions.contingency_rate);

  const profitAmount = (revenueTarget * profitMargin) / 100;
  const overheadAmount = (totalBudget * overheadRate) / 100;
  const contingencyAmount = (totalBudget * contingencyRate) / 100;

  return {
    profitAmount,
    overheadAmount,
    contingencyAmount,
    netBudget: totalBudget - overheadAmount - contingencyAmount,
  };
}

export type BudgetDerivedRates = {
  /** Derived hourly labor rate: labor_allocation / crew_man_hours */
  laborHourlyRate: number | null;
  /** Labor burden rate as decimal (e.g. 0.25 for 25%), passthrough from assumptions */
  laborBurdenRate?: number | null;
  /** Overhead rate as decimal: overhead_allocation / total_direct_costs */
  overheadRate: number;
  /** Profit margin as decimal (% of price): (revenue - total_costs) / revenue */
  profitMargin: number;
  /** All original derived values */
  base: BudgetDerivedValues;
};

/**
 * Derive labor hourly rate, overhead rate, and profit margin from budget allocations.
 * Returns null for laborHourlyRate if inputs are insufficient.
 */
export function deriveBudgetRates(
  assumptions: BudgetAssumptionsInput,
): BudgetDerivedRates {
  const base = calculateBudgetDerivedValues(assumptions);

  const laborAllocation = safeNumber(assumptions.labor_allocation);
  const materialAllocation = safeNumber(assumptions.material_allocation);
  const equipmentAllocation = safeNumber(assumptions.equipment_allocation);
  const subcontractorAllocation = safeNumber(
    assumptions.subcontractor_allocation,
  );
  const overheadAllocation = safeNumber(assumptions.overhead_allocation);
  const crewManHours = safeNumber(assumptions.crew_man_hours);
  const revenueTarget = safeNumber(assumptions.revenue_target);

  const blendedLaborRate = safeNumber(assumptions.blended_labor_rate);
  const laborBudgetMode = assumptions.labor_budget_mode ?? "individual";

  const laborHourlyRate =
    laborBudgetMode === "blended" && blendedLaborRate > 0
      ? blendedLaborRate
      : crewManHours > 0
        ? laborAllocation / crewManHours
        : null;

  const totalDirectCosts =
    laborAllocation +
    materialAllocation +
    equipmentAllocation +
    subcontractorAllocation;

  const overheadRate =
    totalDirectCosts > 0 ? overheadAllocation / totalDirectCosts : 0;

  const totalCosts = totalDirectCosts + overheadAllocation;

  const rawMargin =
    revenueTarget > 0 ? (revenueTarget - totalCosts) / revenueTarget : 0;
  const profitMargin = Math.min(Math.max(rawMargin, 0), 0.99);

  const rawBurden = safeNumber(assumptions.labor_burden_rate);
  const laborBurdenRate =
    rawBurden > 0 ? (rawBurden > 1 ? rawBurden / 100 : rawBurden) : undefined;

  return {
    laborHourlyRate,
    laborBurdenRate,
    overheadRate,
    profitMargin,
    base,
  };
}

/**
 * Compute cost distribution from budget lines (level-1 COGS by dimension_type)
 * and save it into the budget's assumptions JSONB.
 *
 * Call this after any budget line create/update/delete to keep assumptions in sync.
 */
export async function syncCostDistribution(
  supabase: SupabaseClient<Database>,
  budgetId: string,
  organizationDbId: string,
): Promise<void> {
  // Fetch level-1 COGS lines grouped by dimension_type
  const { data: cogsLines, error: linesError } = await supabase
    .from("budget_lines")
    .select("dimension_type, amount, level")
    .eq("budget_id", budgetId)
    .eq("organization_id", organizationDbId)
    .eq("account_type", "cost_of_goods_sold")
    .eq("level", 1);

  if (linesError || !cogsLines || cogsLines.length === 0) return;

  // Filter out "other" dimension — only track materials, labor, equipment, subcontractors
  const relevantLines = cogsLines.filter(
    (l) => l.dimension_type && l.dimension_type !== "other",
  );
  const totalCogs = relevantLines.reduce((sum, l) => sum + (l.amount ?? 0), 0);
  if (totalCogs <= 0) return;

  const byDimension = (dim: string): number | undefined => {
    const amt = relevantLines
      .filter((l) => l.dimension_type === dim)
      .reduce((sum, l) => sum + (l.amount ?? 0), 0);
    return amt > 0 ? Math.round((amt / totalCogs) * 10000) / 10000 : undefined;
  };

  const costDistribution = {
    materials: byDimension("materials"),
    labor: byDimension("labor"),
    equipment: byDimension("equipment"),
    subcontractors: byDimension("subcontractors"),
  };

  // Fetch current assumptions and merge
  const { data: budget, error: budgetError } = await supabase
    .from("budgets")
    .select("assumptions")
    .eq("budget_id", budgetId)
    .eq("organization_id", organizationDbId)
    .single();

  if (budgetError || !budget) return;

  const currentAssumptions =
    budget.assumptions && typeof budget.assumptions === "object"
      ? (budget.assumptions as Record<string, unknown>)
      : {};

  await supabase
    .from("budgets")
    .update({
      assumptions: {
        ...currentAssumptions,
        cost_distribution: costDistribution,
      } as Database["public"]["Tables"]["budgets"]["Row"]["assumptions"],
    })
    .eq("budget_id", budgetId)
    .eq("organization_id", organizationDbId);

  logger.info("Synced cost distribution to budget assumptions", {
    budgetId,
    costDistribution,
  });
}
